import { useMemo, useState } from 'react';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { ESQUEMA_PENDIENTES } from '@/lib/filtros-esquemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, PhoneOff, Sparkles } from 'lucide-react';
import { Badge, Button, Card, CardBody, Input, Modal } from '@/components/ui';
import { useToast } from '@/hooks/useToast';
import {
  listarPendientes,
  kpisPendientes,
  actualizarPendiente,
  type Pendiente,
  type PendienteEstado,
} from '@/api/pendientes.api';
import { urgenciaDePendiente, COLOR_URGENCIA, sinDestino } from '@/lib/pendientes-semaforo';
import { cn } from '@/lib/cn';
import { FiltrosGuardados } from '@/components/filtros/FiltrosGuardados';
import { Cargando } from '@/components/common/Cargando';

/**
 * PENDIENTES DEL VENDEDOR — el tablero (F7).
 *
 * Lo que el supervisor necesita ver de un vistazo: qué está por vencer, quién lo
 * tiene, y a cuáles NO se les va a poder avisar porque falta un teléfono.
 *
 * El color y el "sin destino" salen de `lib/pendientes-semaforo`, que está
 * probado aparte: son las dos decisiones que cambian a qué le presta atención
 * una persona.
 */

const ESTADOS: { valor: PendienteEstado | 'TODOS'; etiqueta: string }[] = [
  { valor: 'TODOS', etiqueta: 'Todos' },
  { valor: 'PROGRAMADO', etiqueta: 'Programados' },
  { valor: 'AVISADO', etiqueta: 'Avisados' },
  { valor: 'RESPONDIDO', etiqueta: 'Respondidos' },
  { valor: 'VENCIDO', etiqueta: 'Vencidos' },
  { valor: 'CUMPLIDO', etiqueta: 'Cumplidos' },
];

const TIPO_LABEL: Record<string, string> = {
  LLAMAR: 'Llamar',
  ESCRIBIR: 'Escribir',
  ENVIAR_INFO: 'Enviar info',
  COTIZACION: 'Cotización',
  SEGUIMIENTO: 'Seguimiento',
  OTRO: 'Otro',
};

export default function Pendientes() {
  const qc = useQueryClient();
  const toast = useToast();
  // A1: los filtros viven en la URL. "TODOS" es el default, asi que se
  // representa como ausencia del parametro.
  const { filtros: filtrosUrl, setFiltros } = useFiltrosUrl<{
    estado?: PendienteEstado;
    ventas?: boolean;
  }>(ESQUEMA_PENDIENTES);
  const estado: PendienteEstado | 'TODOS' = filtrosUrl.estado ?? 'TODOS';
  const setEstado = (v: PendienteEstado | 'TODOS') =>
    setFiltros({ estado: v === 'TODOS' ? undefined : v });
  const soloVentas = filtrosUrl.ventas === true;
  const setSoloVentas = (v: boolean) => setFiltros({ ventas: v || undefined });
  const [descartando, setDescartando] = useState<Pendiente | null>(null);
  const [motivo, setMotivo] = useState('');
  const [reprogramando, setReprogramando] = useState<Pendiente | null>(null);
  const [nuevaFecha, setNuevaFecha] = useState('');

  const filtros = useMemo(
    () => ({
      ...(estado !== 'TODOS' ? { estado } : {}),
      ...(soloVentas ? { esVenta: true } : {}),
    }),
    [estado, soloVentas],
  );

  const lista = useQuery({
    queryKey: ['pendientes', filtros],
    queryFn: () => listarPendientes(filtros),
  });
  const kpis = useQuery({ queryKey: ['pendientes', 'kpis'], queryFn: kpisPendientes });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['pendientes'] });
  };

  const editar = useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Parameters<typeof actualizarPendiente>[1]) =>
      actualizarPendiente(id, input),
    onSuccess: () => {
      invalidar();
      setDescartando(null);
      setReprogramando(null);
      setMotivo('');
      toast.success('Pendiente actualizado');
    },
    onError: () => toast.error('No se pudo actualizar el pendiente'),
  });

  // Los vencidos y los que están por vencer primero: es el orden en que hay que
  // atenderlos, no el de la base.
  const ordenados = useMemo(() => {
    const items = lista.data ?? [];
    return [...items].sort((a, b) => {
      const ua = urgenciaDePendiente(a).orden;
      const ub = urgenciaDePendiente(b).orden;
      if (ua !== ub) return ua - ub;
      return new Date(a.fechaCompromiso).getTime() - new Date(b.fechaCompromiso).getTime();
    });
  }, [lista.data]);

  const sinTelefono = ordenados.filter(sinDestino).length;

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Pendientes del vendedor</h1>
        <p className="text-sm text-slate-500">
          Compromisos que quedaron con clientes y todavía no se cumplieron.
        </p>
      </div>

      {/* ------------------------------------------------------- KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi titulo="Detectados (7 días)" valor={kpis.data?.detectados7d} icono={<Sparkles />} />
        <Kpi titulo="Abiertos ahora" valor={kpis.data?.abiertos} icono={<Clock />} />
        <Kpi
          titulo="Vencidos"
          valor={kpis.data?.vencidos}
          icono={<AlertTriangle />}
          alerta={(kpis.data?.vencidos ?? 0) > 0}
        />
        <Kpi titulo="Cumplidos (7 días)" valor={kpis.data?.cumplidos7d} icono={<CheckCircle2 />} />
      </div>

      {/* A2 · Filtros guardados. Lee la ruta y el ?query de la URL por su cuenta. */}
      <FiltrosGuardados />

      {/* Un pendiente al que no se le puede avisar se ve igual que uno normal
          desde afuera. Por eso se dice acá arriba y no en una fila perdida. */}
      {sinTelefono > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <PhoneOff className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>{sinTelefono}</strong> pendiente(s) no se van a poder avisar: el vendedor no
            tiene teléfono cargado, o el chat no está asignado a nadie. Se carga en Usuarios.
          </span>
        </div>
      )}

      {/* ---------------------------------------------------- filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-surface-border bg-white p-1">
          {ESTADOS.map((e) => (
            <button
              key={e.valor}
              type="button"
              onClick={() => setEstado(e.valor)}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                estado === e.valor
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100',
              )}
            >
              {e.etiqueta}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setSoloVentas(!soloVentas)}
          aria-pressed={soloVentas}
          className={cn(
            'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
            soloVentas
              ? 'border-emerald-600 bg-emerald-600 text-white'
              : 'border-dashed border-emerald-500 bg-white text-emerald-700 hover:bg-emerald-50',
          )}
        >
          Solo ventas
        </button>
      </div>

      {/* ----------------------------------------------------- tabla */}
      <Card>
        <CardBody className="p-0">
          {lista.isLoading ? (
            <Cargando />
          ) : ordenados.length === 0 ? (
            /* A7 · Un vacío dice qué hacer. "No hay pendientes" deja a la
               persona sin saber si el módulo está apagado, si no detectó nada
               todavía, o si los filtros no dejan pasar nada. */
            <p className="p-8 text-center text-sm text-slate-500">
              No hay pendientes con estos filtros.
              <br />
              <span className="text-xs">
                Prueba quitando filtros. Si nunca aparece ninguno, puede que el módulo esté apagado:
                se enciende en <strong>Mi negocio → Pendientes</strong>.
              </span>
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {ordenados.map((p) => {
                const u = urgenciaDePendiente(p);
                const cliente = p.contact?.name ?? p.contact?.pushName ?? p.contact?.phone ?? '—';
                const abierto = !['CUMPLIDO', 'DESCARTADO'].includes(p.estado);
                return (
                  <li key={p.id} className="flex flex-wrap items-start gap-3 p-3">
                    <span
                      className={cn(
                        'rounded-md border px-2 py-1 text-xs font-semibold',
                        COLOR_URGENCIA[u.nivel],
                      )}
                    >
                      {u.etiqueta}
                    </span>

                    <div className="min-w-[16rem] flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-slate-500">{p.codigo}</span>
                        {p.esVenta && <Badge variant="success">VENTA</Badge>}
                        <Badge variant="default">{TIPO_LABEL[p.tipo] ?? p.tipo}</Badge>
                        {p.origen === 'MANUAL' && <Badge variant="default">Manual</Badge>}
                        {sinDestino(p) && (
                          <Badge variant="warning" title="El vendedor no tiene teléfono cargado">
                            Sin destino
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-slate-800">{p.descripcion}</p>
                      <p className="text-xs text-slate-500">
                        {cliente} · vence {new Date(p.fechaCompromiso).toLocaleString('es-DO')} ·{' '}
                        {p.vendedor?.name ?? p.vendedor?.email ?? 'sin vendedor'}
                      </p>
                      {/* La evidencia es lo que hace reclamable un pendiente: sin
                          la cita, nadie sabe de dónde salió. */}
                      {p.evidenciaJson?.quote && (
                        <p className="mt-1 border-l-2 border-slate-200 pl-2 text-xs italic text-slate-500">
                          “{p.evidenciaJson.quote}”
                        </p>
                      )}
                    </div>

                    {abierto && (
                      <div className="flex shrink-0 gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => editar.mutate({ id: p.id, estado: 'CUMPLIDO' })}
                        >
                          Cumplido
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setReprogramando(p);
                            setNuevaFecha(p.fechaCompromiso.slice(0, 16));
                          }}
                        >
                          Reprogramar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDescartando(p)}>
                          Descartar
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* ------------------------------------- cumplimiento por vendedor */}
      {(kpis.data?.porVendedor.length ?? 0) > 0 && (
        <Card>
          <CardBody>
            <h2 className="mb-2 text-sm font-semibold text-slate-800">
              Cumplimiento por vendedor (7 días)
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500">
                  <th className="pb-1">Vendedor</th>
                  <th className="pb-1">Total</th>
                  <th className="pb-1">Cumplidos</th>
                  <th className="pb-1">Vencidos</th>
                  <th className="pb-1">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {kpis.data?.porVendedor.map((v) => (
                  <tr key={v.vendedorUserId ?? 'sin'}>
                    <td className="py-1.5">{v.nombre}</td>
                    <td>{v.total}</td>
                    <td>{v.cumplidos}</td>
                    <td>{v.vencidos}</td>
                    <td>
                      {/* Sin nada cerrado todavía no hay porcentaje que mostrar:
                          un 0% sería mentir sobre alguien que aún está a tiempo. */}
                      {v.porcentaje === null ? (
                        <span className="text-slate-400">todavía sin cerrar</span>
                      ) : (
                        `${v.porcentaje}%`
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {/* -------------------------------------------------- modales */}
      <Modal
        open={Boolean(descartando)}
        onClose={() => setDescartando(null)}
        title={`Descartar ${descartando?.codigo ?? ''}`}
        size="sm"
        description="Queda registrado quién lo descartó y por qué."
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setDescartando(null)}>
              Volver
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={!motivo.trim()}
              loading={editar.isPending}
              onClick={() =>
                descartando &&
                editar.mutate({ id: descartando.id, estado: 'DESCARTADO', motivo: motivo.trim() })
              }
            >
              Descartar
            </Button>
          </>
        }
      >
        <Input
          placeholder="¿Por qué se descarta?"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </Modal>

      <Modal
        open={Boolean(reprogramando)}
        onClose={() => setReprogramando(null)}
        title={`Reprogramar ${reprogramando?.codigo ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setReprogramando(null)}>
              Volver
            </Button>
            <Button
              size="sm"
              disabled={!nuevaFecha}
              loading={editar.isPending}
              onClick={() =>
                reprogramando &&
                editar.mutate({
                  id: reprogramando.id,
                  fechaCompromiso: new Date(nuevaFecha).toISOString(),
                  // Vuelve a PROGRAMADO: si ya se avisó, el aviso viejo quedó
                  // obsoleto y hay que avisar de nuevo en la fecha nueva.
                  estado: 'PROGRAMADO',
                })
              }
            >
              Guardar
            </Button>
          </>
        }
      >
        <Input
          type="datetime-local"
          value={nuevaFecha}
          onChange={(e) => setNuevaFecha(e.target.value)}
        />
      </Modal>
    </div>
  );
}

function Kpi(props: {
  titulo: string;
  valor: number | undefined;
  icono: React.ReactNode;
  alerta?: boolean;
}) {
  return (
    <Card>
      <CardBody className="flex items-center gap-3 p-3">
        <span className={cn('shrink-0', props.alerta ? 'text-red-600' : 'text-slate-400')}>
          {props.icono}
        </span>
        <span>
          <span className="block text-xs text-slate-500">{props.titulo}</span>
          <span
            className={cn(
              'text-xl font-semibold',
              props.alerta ? 'text-red-700' : 'text-slate-900',
            )}
          >
            {props.valor ?? '—'}
          </span>
        </span>
      </CardBody>
    </Card>
  );
}
